/**
 * Translations of the messages the API returns (errors and validation), keyed
 * by the exact English text. `scripts/check-api-messages.mjs` checks that
 * every message in the API and shared schemas is covered; English needs no
 * entry.
 */
export const apiMessages: Record<"de" | "fr" | "es", Record<string, string>> = {
  de: {
    "A QR-IBAN requires QRR references":
      "Eine QR-IBAN erfordert QRR-Referenzen",
    "A catalog item with this SKU already exists":
      "Ein Artikel mit dieser Artikelnummer existiert bereits",
    "A catalog item with this barcode already exists":
      "Ein Artikel mit diesem Barcode existiert bereits",
    "A company holiday already exists on this date":
      "An diesem Datum gibt es bereits einen Feiertag",
    "A day of month is required": "Ein Tag im Monat ist erforderlich",
    "A document file is required": "Eine Dokumentdatei ist erforderlich",
    "A file is required": "Eine Datei ist erforderlich",
    "A lead must be a member of the organization unit":
      "Eine Leitung muss Mitglied der Organisationseinheit sein",
    "A location with inventory cannot be deactivated":
      "Ein Lagerort mit Bestand kann nicht deaktiviert werden",
    "A logo file is required": "Eine Logodatei ist erforderlich",
    "A month is required": "Ein Monat ist erforderlich",
    "A newer revision of this offer exists; decide on the latest revision":
      "Es gibt eine neuere Version dieser Offerte; entscheiden Sie über die neueste",
    "A number sequence is not configured":
      "Ein Nummernkreis ist nicht eingerichtet",
    "A package cannot contain itself":
      "Ein Paket kann sich nicht selbst enthalten",
    "A project cannot be its own parent":
      "Ein Projekt kann nicht sein eigenes Hauptprojekt sein",
    "A reason is required": "Eine Begründung ist erforderlich",
    "A receipt file is required": "Eine Belegdatei ist erforderlich",
    "A receipt unit cost is required": "Ein Einstandspreis ist erforderlich",
    "A rejection reason is required":
      "Eine Begründung für die Zurückweisung ist erforderlich",
    "A revision of this offer already exists":
      "Es gibt bereits eine neue Version dieser Offerte",
    "A role with this name already exists":
      "Eine Rolle mit diesem Namen existiert bereits",
    "A terminal project cannot be scheduled":
      "Ein abgeschlossenes oder abgebrochenes Projekt kann nicht eingeplant werden",
    "A terminal project cannot receive time allocations":
      "Auf ein abgeschlossenes oder abgebrochenes Projekt kann keine Zeit gebucht werden",
    "A terminal project's time cannot be changed":
      "Die Zeit eines abgeschlossenen oder abgebrochenen Projekts kann nicht geändert werden",
    "A unit with this code already exists":
      "Eine Einheit mit diesem Kürzel existiert bereits",
    "A user cannot be both a manager and a member":
      "Eine Person kann nicht gleichzeitig Projektleitung und Mitglied sein",
    "A user with this email already exists":
      "Es gibt bereits eine Person mit dieser E-Mail-Adresse",
    "A weekday is required": "Ein Wochentag ist erforderlich",
    "AI assistance is not configured.":
      "Die KI-Unterstützung ist nicht eingerichtet.",
    "API key not found": "API-Schlüssel nicht gefunden",
    "Add at least one expense before submitting":
      "Erfassen Sie vor dem Einreichen mindestens eine Ausgabe",
    "Add at least one priced line before issuing":
      "Erfassen Sie vor dem Ausstellen mindestens eine Position mit Preis",
    "Add at least one priced line before sending the offer":
      "Erfassen Sie vor dem Senden mindestens eine Position mit Preis",
    "Administrator access is required": "Dafür sind Administratorrechte nötig",
    "All expense items must use the claim currency":
      "Alle Positionen müssen in der Währung der Abrechnung sein",
    "Allocation date must fall within the timesheet week":
      "Das Datum muss in der Woche der Zeiterfassung liegen",
    "Amount must be a non-negative decimal":
      "Der Betrag muss eine positive Zahl sein",
    "Amount must be greater than zero": "Der Betrag muss grösser als null sein",
    "Amount must be nonnegative": "Der Betrag darf nicht negativ sein",
    "An audio file is required in the file field.":
      "Eine Audiodatei ist erforderlich.",
    "An invoice line references a line of another offer":
      "Eine Rechnungsposition verweist auf eine andere Offerte",
    "An ordinal is required":
      "Eine Reihenfolge (z. B. erster, zweiter) ist erforderlich",
    "An organization unit cannot contain itself":
      "Eine Organisationseinheit kann sich nicht selbst enthalten",
    "Assignment range must contain between 1 and 42 days":
      "Der Zeitraum muss zwischen 1 und 42 Tagen liegen",
    "Attachment not found": "Anhang nicht gefunden",
    "Attendance intervals cannot overlap":
      "Arbeitszeiten dürfen sich nicht überschneiden",
    "Audio file is too large.": "Die Audiodatei ist zu gross.",
    "Billing settings not found": "Rechnungseinstellungen nicht gefunden",
    "Calendar end must not precede start":
      "Das Ende darf nicht vor dem Anfang liegen",
    "Calendar event end must be after start":
      "Das Ende muss nach dem Anfang liegen",
    "Calendar feed not found": "Kalender-Abo nicht gefunden",
    "Calendar range cannot exceed one year":
      "Der Zeitraum darf höchstens ein Jahr umfassen",
    "Catalog item is not enabled for stock":
      "Für diesen Artikel wird kein Bestand geführt",
    "Catalog item not found": "Artikel nicht gefunden",
    "Categories support only one subcategory level":
      "Kategorien unterstützen nur eine Ebene von Unterkategorien",
    "Category not found": "Kategorie nicht gefunden",
    "Choose either an offer or a request as the source":
      "Wählen Sie entweder eine Offerte oder eine Anfrage als Grundlage",
    "Clock-out time must be after clock-in time":
      "Das Ausstempeln muss nach dem Einstempeln liegen",
    "Closed requests cannot be edited. Reopen it first.":
      "Abgeschlossene Anfragen können nicht bearbeitet werden. Öffnen Sie sie zuerst wieder.",
    "Company holiday not found": "Feiertag nicht gefunden",
    "Company logo not found": "Firmenlogo nicht gefunden",
    "Company name is required for company customers":
      "Für Firmenkunden ist ein Firmenname erforderlich",
    "Company settings not found": "Firmeneinstellungen nicht gefunden",
    "Completed or cancelled projects cannot receive time entries":
      "Auf abgeschlossene oder abgebrochene Projekte kann keine Zeit gebucht werden",
    "Configure the bank account (IBAN) in the billing settings before issuing invoices":
      "Hinterlegen Sie vor dem Ausstellen von Rechnungen das Bankkonto (IBAN) in den Rechnungseinstellungen",
    "Credit notes can only correct invoices":
      "Gutschriften können nur Rechnungen korrigieren",
    "Customer not found": "Kunde nicht gefunden",
    "Customer, request or accepted offer is required":
      "Ein Kunde, eine Anfrage oder eine angenommene Offerte ist erforderlich",
    "Date is not valid": "Das Datum ist ungültig",
    "Date must use YYYY-MM-DD": "Das Datum muss im Format JJJJ-MM-TT sein",
    "Deactivated employees must be reactivated instead":
      "Deaktivierte Mitarbeitende müssen stattdessen reaktiviert werden",
    "Default inventory location is inactive or missing":
      "Der Standard-Lagerort ist inaktiv oder fehlt",
    "Discount cannot exceed 100%": "Der Rabatt darf 100 % nicht übersteigen",
    "Document category not found": "Dokumentkategorie nicht gefunden",
    "Document not found": "Dokument nicht gefunden",
    "Document settings not found": "Dokumenteinstellungen nicht gefunden",
    "Duration is required": "Eine Dauer ist erforderlich",
    "Each weekday is required once":
      "Jeder Wochentag muss genau einmal vorkommen",
    "Email confirmation link is invalid or has expired":
      "Der Bestätigungslink ist ungültig oder abgelaufen",
    "Employee document not found": "Dokument nicht gefunden",
    "Employee is already deactivated": "Die Person ist bereits deaktiviert",
    "Employee not found": "Mitarbeitende Person nicht gefunden",
    "Employee number or AHV/AVS number is already assigned":
      "Personalnummer oder AHV-Nummer ist bereits vergeben",
    "Employment end must not precede employment start":
      "Der Austritt darf nicht vor dem Eintritt liegen",
    "End time must be after start time":
      "Die Endzeit muss nach der Startzeit liegen",
    "Everything on this offer has already been invoiced":
      "Diese Offerte ist bereits vollständig verrechnet",
    "Expense claim not found": "Spesenabrechnung nicht gefunden",
    "Expense claim was changed; reload before saving":
      "Die Spesenabrechnung wurde geändert; laden Sie die Seite neu",
    "Failed email not found": "Fehlgeschlagene E-Mail nicht gefunden",
    "File not found": "Datei nicht gefunden",
    "Financial request details require finance access":
      "Finanzangaben der Anfrage erfordern Finanzrechte",
    "Fixed amount is required for fixed-price requests":
      "Bei Pauschalpreis ist ein Betrag erforderlich",
    "Insufficient inventory on hand": "Nicht genügend Bestand",
    "Insufficient permissions": "Dafür fehlen Ihnen die Rechte",
    "Insufficient permissions to assign requests":
      "Sie dürfen keine Anfragen zuweisen",
    "Invalid AHV/AVS number": "Ungültige AHV-Nummer",
    "Invalid IBAN checksum": "Die IBAN ist ungültig (Prüfziffer)",
    "Invalid balance year": "Ungültiges Jahr",
    "Invalid decimal value": "Ungültige Zahl",
    "Invalid login identifier or password":
      "Benutzername oder Passwort ist falsch",
    "Invalid or expired setup invitation":
      "Die Einrichtungseinladung ist ungültig oder abgelaufen",
    "Invalid phone number": "Ungültige Telefonnummer",
    "Invalid setup token": "Ungültiges Einrichtungstoken",
    "Invalid year": "Ungültiges Jahr",
    "Inventory balance not found": "Bestand nicht gefunden",
    "Inventory cannot be issued to a terminal project":
      "An ein abgeschlossenes oder abgebrochenes Projekt kann kein Material ausgegeben werden",
    "Inventory cannot be returned to a terminal project":
      "Von einem abgeschlossenen oder abgebrochenen Projekt kann kein Material zurückgenommen werden",
    "Inventory location not found": "Lagerort nicht gefunden",
    "Invitation is invalid or has expired":
      "Die Einladung ist ungültig oder abgelaufen",
    "Invoice PDF not found": "Rechnungs-PDF nicht gefunden",
    "Invoice not found": "Rechnung nicht gefunden",
    "Invoice share link not found": "Freigabelink nicht gefunden",
    "Issued invoices cannot be edited. Issue a credit note to correct them.":
      "Ausgestellte Rechnungen können nicht bearbeitet werden. Korrigieren Sie sie mit einer Gutschrift.",
    "Localization settings not found": "Spracheinstellungen nicht gefunden",
    "Login identifier is required": "Benutzername oder E-Mail ist erforderlich",
    "Logo must be a JPEG, PNG or WebP image":
      "Das Logo muss ein JPEG-, PNG- oder WebP-Bild sein",
    "Mileage expenses require kilometers":
      "Für Kilometerspesen sind Kilometer erforderlich",
    "Missing or invalid CSRF token":
      "Die Sitzung ist abgelaufen; laden Sie die Seite neu",
    "New password must differ from the current password":
      "Das neue Passwort muss sich vom aktuellen unterscheiden",
    "No impersonation session is active":
      "Es ist keine Stellvertretungssitzung aktiv",
    "No scheduled offboarding found": "Kein geplanter Austritt gefunden",
    "Not available while impersonating an employee":
      "Während der Stellvertretung nicht verfügbar",
    "Not found": "Nicht gefunden",
    "Notification not found": "Benachrichtigung nicht gefunden",
    "Number sequence not found": "Nummernkreis nicht gefunden",
    "Offer PDF not found": "Offerten-PDF nicht gefunden",
    "Offer customer and currency must match":
      "Kunde und Währung der Offerte müssen übereinstimmen",
    "Offer line references require an offer as the source":
      "Verweise auf Offertpositionen erfordern eine Offerte als Grundlage",
    "Offer not found": "Offerte nicht gefunden",
    "One or more assigned users were not found":
      "Eine oder mehrere zugewiesene Personen wurden nicht gefunden",
    "One or more organization units were not found or inactive":
      "Eine oder mehrere Organisationseinheiten fehlen oder sind inaktiv",
    "One or more organization users were not found":
      "Eine oder mehrere Personen wurden nicht gefunden",
    "One or more project members were not found":
      "Eine oder mehrere Projektmitglieder wurden nicht gefunden",
    "One or more roles were not found":
      "Eine oder mehrere Rollen wurden nicht gefunden",
    "Only JPEG, PNG, WebP and PDF files are supported":
      "Nur JPEG-, PNG-, WebP- und PDF-Dateien werden unterstützt",
    "Only PDF, DOCX, JPEG, PNG and WebP files are supported":
      "Nur PDF-, DOCX-, JPEG-, PNG- und WebP-Dateien werden unterstützt",
    "Only a planned project can be deleted":
      "Nur geplante Projekte können gelöscht werden",
    "Only a submitted timesheet can be withdrawn":
      "Nur eine eingereichte Zeiterfassung kann zurückgezogen werden",
    "Only accepted offers can be invoiced":
      "Nur angenommene Offerten können verrechnet werden",
    "Only active email-based users can receive a password reset":
      "Nur aktive Personen mit E-Mail-Adresse können ein neues Passwort anfordern",
    "Only administrators can change administrator accounts":
      "Nur Administratoren können Administratorkonten ändern",
    "Only administrators can grant administrator rights":
      "Nur Administratoren können Administratorrechte vergeben",
    "Only an accepted offer can start a project":
      "Nur aus einer angenommenen Offerte kann ein Projekt entstehen",
    "Only approved timesheets can be reopened":
      "Nur genehmigte Zeiterfassungen können wieder geöffnet werden",
    "Only deactivated employees can be reactivated":
      "Nur deaktivierte Personen können reaktiviert werden",
    "Only dead-lettered deliveries can be retried":
      "Nur endgültig fehlgeschlagene Zustellungen können wiederholt werden",
    "Only draft expense claims can be changed":
      "Nur Spesenentwürfe können geändert werden",
    "Only draft expense claims can be submitted":
      "Nur Spesenentwürfe können eingereicht werden",
    "Only draft invoices can be deleted":
      "Nur Rechnungsentwürfe können gelöscht werden",
    "Only draft invoices can be issued":
      "Nur Rechnungsentwürfe können ausgestellt werden",
    "Only draft offers can be deleted":
      "Nur Offertentwürfe können gelöscht werden",
    "Only draft offers can be edited. Create a revision to change a sent offer.":
      "Nur Offertentwürfe können bearbeitet werden. Erstellen Sie eine neue Version, um eine gesendete Offerte zu ändern.",
    "Only invited users can receive a new invitation":
      "Nur eingeladene Personen können eine neue Einladung erhalten",
    "Only issued invoices can be credited":
      "Nur ausgestellte Rechnungen können gutgeschrieben werden",
    "Only issued invoices can be shared":
      "Nur ausgestellte Rechnungen können geteilt werden",
    "Only packages can contain component items":
      "Nur Pakete können Bestandteile enthalten",
    "Only packages can contain items": "Nur Pakete können Artikel enthalten",
    "Only personal details can be changed":
      "Sie können nur Ihre persönlichen Angaben ändern",
    "Only submitted expense claims can be processed":
      "Nur eingereichte Spesenabrechnungen können bearbeitet werden",
    "Only submitted expense claims can be withdrawn":
      "Nur eingereichte Spesenabrechnungen können zurückgezogen werden",
    "Only submitted timesheets can be approved or rejected":
      "Nur eingereichte Zeiterfassungen können genehmigt oder zurückgewiesen werden",
    "Organization unit not found": "Organisationseinheit nicht gefunden",
    "Organization units cannot contain circular ancestry":
      "Organisationseinheiten dürfen keine zirkuläre Hierarchie haben",
    "Package components must be active inventory items":
      "Paketbestandteile müssen aktive Lagerartikel sein",
    "Package items must be unique":
      "Paketbestandteile dürfen nur einmal vorkommen",
    "Parent category not found": "Übergeordnete Kategorie nicht gefunden",
    "Parent organization unit not found":
      "Übergeordnete Organisationseinheit nicht gefunden",
    "Parent project customer and currency must match":
      "Kunde und Währung des Hauptprojekts müssen übereinstimmen",
    "Parent project not found": "Hauptprojekt nicht gefunden",
    "Password must be at least 10 characters":
      "Das Passwort muss mindestens 10 Zeichen lang sein",
    "Password must contain a letter":
      "Das Passwort muss einen Buchstaben enthalten",
    "Password must contain a number": "Das Passwort muss eine Zahl enthalten",
    "Payment not found": "Zahlung nicht gefunden",
    "Payments can only be recorded on issued invoices":
      "Zahlungen können nur auf ausgestellten Rechnungen erfasst werden",
    "Payments cannot be recorded on credit notes":
      "Auf Gutschriften können keine Zahlungen erfasst werden",
    "Payments on cancelled invoices cannot be changed":
      "Zahlungen auf stornierten Rechnungen können nicht geändert werden",
    "Planned end must not be before planned start":
      "Das geplante Ende darf nicht vor dem Start liegen",
    "Planner assignment not found": "Einsatz nicht gefunden",
    "Planner range is required": "Ein Zeitraum ist erforderlich",
    "Planner range must cover 1 to 35 days":
      "Der Zeitraum muss 1 bis 35 Tage umfassen",
    "Project cost not found": "Projektkosten nicht gefunden",
    "Project customer and currency must match the invoice":
      "Kunde und Währung des Projekts müssen zur Rechnung passen",
    "Project customer and currency must match the offer":
      "Kunde und Währung des Projekts müssen zur Offerte passen",
    "Project has related records; cancel it instead":
      "Das Projekt hat bereits Einträge; brechen Sie es stattdessen ab",
    "Project hierarchy cannot contain a cycle":
      "Die Projekthierarchie darf keinen Kreis enthalten",
    "Project hierarchy is inconsistent":
      "Die Projekthierarchie ist inkonsistent",
    "Project not found": "Projekt nicht gefunden",
    "Project time cannot exceed worked time for this day":
      "Die Projektzeit darf die Arbeitszeit des Tages nicht übersteigen",
    "QRR references require a QR-IBAN": "QRR-Referenzen erfordern eine QR-IBAN",
    "Quantity must be greater than zero":
      "Die Menge muss grösser als null sein",
    "Quantity must be nonnegative": "Die Menge darf nicht negativ sein",
    "Quantity must be positive": "Die Menge muss positiv sein",
    "Receipt not found": "Beleg nicht gefunden",
    "Receipts can only be changed on draft claims":
      "Belege können nur bei Entwürfen geändert werden",
    "Receipts of paid claims cannot be changed":
      "Belege bezahlter Abrechnungen können nicht geändert werden",
    "Removed members still have future schedule assignments":
      "Entfernte Mitglieder haben noch künftige Einsätze",
    "Request not found": "Anfrage nicht gefunden",
    "Requested date is required": "Ein Wunschdatum ist erforderlich",
    "Requests with offers or invoices cannot be deleted. Cancel it instead.":
      "Anfragen mit Offerten oder Rechnungen können nicht gelöscht werden. Brechen Sie sie stattdessen ab.",
    "Resolve stock, pending offers and active package usage before changing inventory tracking":
      "Klären Sie Bestand, offene Offerten und Paketverwendungen, bevor Sie die Lagerführung ändern",
    "Return quantity exceeds the source issue":
      "Die Rücknahme übersteigt die ausgegebene Menge",
    "Role not found": "Rolle nicht gefunden",
    "Roles assigned to users cannot be deleted":
      "Zugewiesene Rollen können nicht gelöscht werden",
    "Session not found": "Sitzung nicht gefunden",
    "Setup has already been completed.":
      "Die Einrichtung ist bereits abgeschlossen.",
    "Site address not found": "Einsatzadresse nicht gefunden",
    "Source and destination locations must differ":
      "Herkunfts- und Ziellagerort müssen verschieden sein",
    "Source issue does not match the return":
      "Die ursprüngliche Ausgabe passt nicht zur Rücknahme",
    "Source request customer and currency must match":
      "Kunde und Währung der Anfrage müssen übereinstimmen",
    "Start and end time are both required":
      "Start- und Endzeit sind erforderlich",
    "Submitted or approved timesheets cannot be changed":
      "Eingereichte oder genehmigte Zeiterfassungen können nicht geändert werden",
    "System admin cannot be invited":
      "Der Systemadministrator kann nicht eingeladen werden",
    "System admin has no email address":
      "Der Systemadministrator hat keine E-Mail-Adresse",
    "System administrator access is required":
      "Dafür sind Systemadministratorrechte nötig",
    "System administrator session is unavailable":
      "Die Sitzung des Systemadministrators ist nicht verfügbar",
    "System roles cannot be deleted":
      "Systemrollen können nicht gelöscht werden",
    "Tax rate not found": "Steuersatz nicht gefunden",
    "Tenant setup has not been completed yet.":
      "Die Einrichtung ist noch nicht abgeschlossen.",
    "The AI provider is temporarily unavailable.":
      "Der KI-Dienst ist vorübergehend nicht erreichbar.",
    "The AI provider returned an invalid response.":
      "Der KI-Dienst hat eine ungültige Antwort geliefert.",
    "The assignment was modified by someone else":
      "Der Einsatz wurde inzwischen von jemand anderem geändert",
    "The audio file is empty.": "Die Audiodatei ist leer.",
    "The corrected invoice is no longer open":
      "Die korrigierte Rechnung ist nicht mehr offen",
    "The credit note exceeds the amount still creditable on the invoice":
      "Die Gutschrift übersteigt den noch gutschreibbaren Betrag",
    "The customer cannot change once offers or invoices exist":
      "Der Kunde kann nicht mehr geändert werden, sobald Offerten oder Rechnungen bestehen",
    "The customer does not match the source document":
      "Der Kunde passt nicht zum Ausgangsdokument",
    "The customer has no billing address":
      "Der Kunde hat keine Rechnungsadresse",
    "The customer has no email address": "Der Kunde hat keine E-Mail-Adresse",
    "The customer needs a billing address":
      "Der Kunde braucht eine Rechnungsadresse",
    "The customer was modified by someone else. Reload and retry.":
      "Der Kunde wurde inzwischen geändert. Laden Sie die Seite neu und versuchen Sie es erneut.",
    "The default location cannot be deactivated":
      "Der Standard-Lagerort kann nicht deaktiviert werden",
    "The default location must be active":
      "Der Standard-Lagerort muss aktiv sein",
    "The default tax rate cannot be deleted":
      "Der Standard-Steuersatz kann nicht gelöscht werden",
    "The existing organization hierarchy contains a cycle":
      "Die bestehende Organisationshierarchie enthält einen Kreis",
    "The file contents do not match the selected file type":
      "Der Dateiinhalt passt nicht zum Dateityp",
    "The image could not be processed. Please upload a JPEG or PNG.":
      "Das Bild konnte nicht verarbeitet werden. Bitte laden Sie ein JPEG oder PNG hoch.",
    "The invoice has payments. Issue a credit note instead of cancelling.":
      "Auf der Rechnung sind Zahlungen erfasst. Erstellen Sie eine Gutschrift statt zu stornieren.",
    "The invoice total must be greater than zero":
      "Der Rechnungsbetrag muss grösser als null sein",
    "The invoice was already partially credited":
      "Die Rechnung wurde bereits teilweise gutgeschrieben",
    "The invoice was already sent": "Die Rechnung wurde bereits gesendet",
    "The invoice was modified by someone else. Reload and try again.":
      "Die Rechnung wurde inzwischen geändert. Laden Sie die Seite neu und versuchen Sie es erneut.",
    "The invoiced quantity exceeds what is left to invoice on the offer":
      "Die Menge übersteigt, was auf der Offerte noch zu verrechnen ist",
    "The last active administrator cannot be deactivated":
      "Der letzte aktive Administrator kann nicht deaktiviert werden",
    "The last active administrator cannot be offboarded":
      "Der letzte aktive Administrator kann nicht austreten",
    "The monthly AI budget has been exhausted.":
      "Das monatliche KI-Budget ist aufgebraucht.",
    "The new email address must differ from the current one":
      "Die neue E-Mail-Adresse muss sich von der aktuellen unterscheiden",
    "The offer already belongs to a project":
      "Die Offerte gehört bereits zu einem Projekt",
    "The offer cannot be decided in its current status":
      "Über die Offerte kann in diesem Status nicht entschieden werden",
    "The offer cannot be revised in its current status":
      "Die Offerte kann in diesem Status nicht überarbeitet werden",
    "The offer cannot be sent in its current status":
      "Die Offerte kann in diesem Status nicht gesendet werden",
    "The offer was already sent": "Die Offerte wurde bereits gesendet",
    "The offer was modified by someone else. Reload and try again.":
      "Die Offerte wurde inzwischen geändert. Laden Sie die Seite neu und versuchen Sie es erneut.",
    "The organization unit was modified by someone else":
      "Die Organisationseinheit wurde inzwischen geändert",
    "The payment was modified by someone else":
      "Die Zahlung wurde inzwischen geändert",
    "The project belongs to another customer":
      "Das Projekt gehört zu einem anderen Kunden",
    "The project budget was modified by someone else":
      "Das Projektbudget wurde inzwischen geändert",
    "The project cost was modified by someone else":
      "Die Projektkosten wurden inzwischen geändert",
    "The project was modified by someone else":
      "Das Projekt wurde inzwischen geändert",
    "The request contains invalid fields.": "Einige Angaben sind ungültig.",
    "The request was modified by someone else. Reload and try again.":
      "Die Anfrage wurde inzwischen geändert. Laden Sie die Seite neu und versuchen Sie es erneut.",
    "The source request is already assigned to a project":
      "Die Anfrage ist bereits einem Projekt zugeordnet",
    "The system administrator account cannot be changed here":
      "Das Systemadministrator-Konto kann hier nicht geändert werden",
    "The time allocation was modified by someone else":
      "Die Zeitzuweisung wurde inzwischen geändert",
    "The timesheet is not editable":
      "Die Zeiterfassung kann nicht bearbeitet werden",
    "The timesheet owner is not a project member":
      "Die Person ist kein Mitglied des Projekts",
    "The wildcard event cannot be combined with other events":
      "«Alle Ereignisse» kann nicht mit einzelnen Ereignissen kombiniert werden",
    "This customer has requests, offers, projects or invoices and cannot be deleted. Archive the customer instead.":
      "Dieser Kunde hat Anfragen, Offerten, Projekte oder Rechnungen und kann nicht gelöscht werden. Archivieren Sie ihn stattdessen.",
    "This status change is not possible":
      "Dieser Statuswechsel ist nicht möglich",
    "Thumbnail not found": "Vorschaubild nicht gefunden",
    "Time allocation not found": "Zeitzuweisung nicht gefunden",
    "Time can only be assigned to your projects":
      "Zeit kann nur auf Ihre Projekte gebucht werden",
    "Time entry not found": "Zeiteintrag nicht gefunden",
    "Time entry was changed by someone else":
      "Der Zeiteintrag wurde inzwischen geändert",
    "Time must use HH:mm": "Die Zeit muss im Format HH:mm sein",
    "Timesheet not found": "Zeiterfassung nicht gefunden",
    "Timesheet was changed; reload before deciding":
      "Die Zeiterfassung wurde geändert; laden Sie die Seite neu",
    "Timesheet was changed; reload before saving":
      "Die Zeiterfassung wurde geändert; laden Sie die Seite neu",
    "Timesheet was changed; reload before submitting":
      "Die Zeiterfassung wurde geändert; laden Sie die Seite neu",
    "Unit not found": "Einheit nicht gefunden",
    "Unknown permission": "Unbekannte Berechtigung",
    "Unsupported audio type. Use WAV, MP3, M4A, OGG, WebM, or FLAC.":
      "Nicht unterstütztes Audioformat. Verwenden Sie WAV, MP3, M4A, OGG, WebM oder FLAC.",
    "Unsupported webhook event": "Unbekanntes Webhook-Ereignis",
    "Use a hex colour like #0f766e": "Verwenden Sie eine Hex-Farbe wie #0f766e",
    "User not found": "Person nicht gefunden",
    "Vacation booking exceeds the available balance":
      "Die Ferienbuchung übersteigt das verfügbare Guthaben",
    "Webhook URL must not contain credentials":
      "Die Webhook-URL darf keine Zugangsdaten enthalten",
    "Webhook URL must use HTTP or HTTPS":
      "Die Webhook-URL muss HTTP oder HTTPS verwenden",
    "Webhook delivery not found": "Webhook-Zustellung nicht gefunden",
    "Webhook endpoint not found": "Webhook-Ziel nicht gefunden",
    "Webhook endpoint was changed; reload before saving":
      "Das Webhook-Ziel wurde geändert; laden Sie die Seite neu",
    "You are already clocked in": "Sie sind bereits eingestempelt",
    "You are not clocked in": "Sie sind nicht eingestempelt",
    "You cannot deactivate your own account":
      "Sie können Ihr eigenes Konto nicht deaktivieren",
    "You cannot grant permissions you do not have":
      "Sie können keine Rechte vergeben, die Sie selbst nicht haben",
    "You cannot offboard your own account":
      "Sie können Ihr eigenes Konto nicht austreten lassen",
    "month must use the YYYY-MM format":
      "Der Monat muss im Format JJJJ-MM sein",
  },
  fr: {
    "A QR-IBAN requires QRR references":
      "Un QR-IBAN nécessite des références QRR",
    "A catalog item with this SKU already exists":
      "Un article avec ce numéro existe déjà",
    "A catalog item with this barcode already exists":
      "Un article avec ce code-barres existe déjà",
    "A company holiday already exists on this date":
      "Un jour férié existe déjà à cette date",
    "A day of month is required": "Un jour du mois est requis",
    "A document file is required": "Un fichier est requis",
    "A file is required": "Un fichier est requis",
    "A lead must be a member of the organization unit":
      "Un responsable doit être membre de l'unité",
    "A location with inventory cannot be deactivated":
      "Un emplacement avec du stock ne peut pas être désactivé",
    "A logo file is required": "Un fichier de logo est requis",
    "A month is required": "Un mois est requis",
    "A newer revision of this offer exists; decide on the latest revision":
      "Une version plus récente de cette offre existe ; décidez sur la dernière",
    "A number sequence is not configured":
      "Une séquence de numérotation n'est pas configurée",
    "A package cannot contain itself":
      "Un lot ne peut pas se contenir lui-même",
    "A project cannot be its own parent":
      "Un projet ne peut pas être son propre parent",
    "A reason is required": "Un motif est requis",
    "A receipt file is required": "Un justificatif est requis",
    "A receipt unit cost is required": "Un coût unitaire est requis",
    "A rejection reason is required": "Un motif de refus est requis",
    "A revision of this offer already exists":
      "Une révision de cette offre existe déjà",
    "A role with this name already exists":
      "Un rôle portant ce nom existe déjà",
    "A terminal project cannot be scheduled":
      "Un projet terminé ou annulé ne peut pas être planifié",
    "A terminal project cannot receive time allocations":
      "Aucun temps ne peut être imputé à un projet terminé ou annulé",
    "A terminal project's time cannot be changed":
      "Le temps d'un projet terminé ou annulé ne peut pas être modifié",
    "A unit with this code already exists":
      "Une unité avec ce code existe déjà",
    "A user cannot be both a manager and a member":
      "Une personne ne peut pas être à la fois responsable et membre",
    "A user with this email already exists":
      "Un utilisateur avec cette adresse e-mail existe déjà",
    "A weekday is required": "Un jour de la semaine est requis",
    "AI assistance is not configured.": "L'assistance IA n'est pas configurée.",
    "API key not found": "Clé API introuvable",
    "Add at least one expense before submitting":
      "Ajoutez au moins une dépense avant de soumettre",
    "Add at least one priced line before issuing":
      "Ajoutez au moins une ligne chiffrée avant d'émettre",
    "Add at least one priced line before sending the offer":
      "Ajoutez au moins une ligne chiffrée avant d'envoyer l'offre",
    "Administrator access is required": "Un accès administrateur est requis",
    "All expense items must use the claim currency":
      "Tous les postes doivent être dans la devise de la note",
    "Allocation date must fall within the timesheet week":
      "La date doit se situer dans la semaine de la feuille de temps",
    "Amount must be a non-negative decimal":
      "Le montant doit être un nombre positif",
    "Amount must be greater than zero": "Le montant doit être supérieur à zéro",
    "Amount must be nonnegative": "Le montant ne peut pas être négatif",
    "An audio file is required in the file field.":
      "Un fichier audio est requis.",
    "An invoice line references a line of another offer":
      "Une ligne de facture fait référence à une autre offre",
    "An ordinal is required": "Un rang (premier, deuxième…) est requis",
    "An organization unit cannot contain itself":
      "Une unité ne peut pas se contenir elle-même",
    "Assignment range must contain between 1 and 42 days":
      "La période doit comporter entre 1 et 42 jours",
    "Attachment not found": "Pièce jointe introuvable",
    "Attendance intervals cannot overlap":
      "Les plages de présence ne peuvent pas se chevaucher",
    "Audio file is too large.": "Le fichier audio est trop volumineux.",
    "Billing settings not found": "Paramètres de facturation introuvables",
    "Calendar end must not precede start":
      "La fin ne peut pas précéder le début",
    "Calendar event end must be after start":
      "La fin doit être postérieure au début",
    "Calendar feed not found": "Abonnement de calendrier introuvable",
    "Calendar range cannot exceed one year":
      "La période ne peut pas dépasser un an",
    "Catalog item is not enabled for stock":
      "Cet article n'est pas géré en stock",
    "Catalog item not found": "Article introuvable",
    "Categories support only one subcategory level":
      "Les catégories n'ont qu'un niveau de sous-catégories",
    "Category not found": "Catégorie introuvable",
    "Choose either an offer or a request as the source":
      "Choisissez soit une offre, soit une demande comme source",
    "Clock-out time must be after clock-in time":
      "L'heure de sortie doit être postérieure à l'heure d'entrée",
    "Closed requests cannot be edited. Reopen it first.":
      "Une demande close ne peut pas être modifiée. Rouvrez-la d'abord.",
    "Company holiday not found": "Jour férié introuvable",
    "Company logo not found": "Logo de l'entreprise introuvable",
    "Company name is required for company customers":
      "Le nom de l'entreprise est requis pour les clients professionnels",
    "Company settings not found": "Paramètres de l'entreprise introuvables",
    "Completed or cancelled projects cannot receive time entries":
      "Aucun temps ne peut être saisi sur un projet terminé ou annulé",
    "Configure the bank account (IBAN) in the billing settings before issuing invoices":
      "Configurez le compte bancaire (IBAN) dans les paramètres de facturation avant d'émettre des factures",
    "Credit notes can only correct invoices":
      "Les notes de crédit ne peuvent corriger que des factures",
    "Customer not found": "Client introuvable",
    "Customer, request or accepted offer is required":
      "Un client, une demande ou une offre acceptée est requis",
    "Date is not valid": "La date n'est pas valide",
    "Date must use YYYY-MM-DD": "La date doit être au format AAAA-MM-JJ",
    "Deactivated employees must be reactivated instead":
      "Les collaborateurs désactivés doivent plutôt être réactivés",
    "Default inventory location is inactive or missing":
      "L'emplacement par défaut est inactif ou manquant",
    "Discount cannot exceed 100%": "La remise ne peut pas dépasser 100 %",
    "Document category not found": "Catégorie de document introuvable",
    "Document not found": "Document introuvable",
    "Document settings not found": "Paramètres des documents introuvables",
    "Duration is required": "Une durée est requise",
    "Each weekday is required once":
      "Chaque jour de la semaine doit figurer une fois",
    "Email confirmation link is invalid or has expired":
      "Le lien de confirmation est invalide ou a expiré",
    "Employee document not found": "Document introuvable",
    "Employee is already deactivated": "La personne est déjà désactivée",
    "Employee not found": "Collaborateur introuvable",
    "Employee number or AHV/AVS number is already assigned":
      "Le numéro de personnel ou AVS est déjà attribué",
    "Employment end must not precede employment start":
      "La fin d'emploi ne peut pas précéder le début",
    "End time must be after start time":
      "L'heure de fin doit être postérieure à l'heure de début",
    "Everything on this offer has already been invoiced":
      "Cette offre a déjà été entièrement facturée",
    "Expense claim not found": "Note de frais introuvable",
    "Expense claim was changed; reload before saving":
      "La note de frais a été modifiée ; rechargez avant d'enregistrer",
    "Failed email not found": "E-mail en échec introuvable",
    "File not found": "Fichier introuvable",
    "Financial request details require finance access":
      "Les détails financiers requièrent un accès aux finances",
    "Fixed amount is required for fixed-price requests":
      "Un montant est requis pour un prix forfaitaire",
    "Insufficient inventory on hand": "Stock insuffisant",
    "Insufficient permissions": "Vous n'avez pas les droits nécessaires",
    "Insufficient permissions to assign requests":
      "Vous ne pouvez pas attribuer des demandes",
    "Invalid AHV/AVS number": "Numéro AVS invalide",
    "Invalid IBAN checksum": "L'IBAN est invalide (clé de contrôle)",
    "Invalid balance year": "Année invalide",
    "Invalid decimal value": "Nombre invalide",
    "Invalid login identifier or password":
      "Identifiant ou mot de passe incorrect",
    "Invalid or expired setup invitation":
      "L'invitation de configuration est invalide ou expirée",
    "Invalid phone number": "Numéro de téléphone invalide",
    "Invalid setup token": "Jeton de configuration invalide",
    "Invalid year": "Année invalide",
    "Inventory balance not found": "Stock introuvable",
    "Inventory cannot be issued to a terminal project":
      "Aucun matériel ne peut être sorti vers un projet terminé ou annulé",
    "Inventory cannot be returned to a terminal project":
      "Aucun matériel ne peut être repris d'un projet terminé ou annulé",
    "Inventory location not found": "Emplacement introuvable",
    "Invitation is invalid or has expired":
      "L'invitation est invalide ou a expiré",
    "Invoice PDF not found": "PDF de la facture introuvable",
    "Invoice not found": "Facture introuvable",
    "Invoice share link not found": "Lien de partage introuvable",
    "Issued invoices cannot be edited. Issue a credit note to correct them.":
      "Une facture émise ne peut pas être modifiée. Corrigez-la avec une note de crédit.",
    "Localization settings not found": "Paramètres régionaux introuvables",
    "Login identifier is required": "Identifiant ou e-mail requis",
    "Logo must be a JPEG, PNG or WebP image":
      "Le logo doit être une image JPEG, PNG ou WebP",
    "Mileage expenses require kilometers":
      "Les frais kilométriques nécessitent des kilomètres",
    "Missing or invalid CSRF token": "La session a expiré ; rechargez la page",
    "New password must differ from the current password":
      "Le nouveau mot de passe doit différer de l'actuel",
    "No impersonation session is active":
      "Aucune session d'emprunt d'identité n'est active",
    "No scheduled offboarding found": "Aucun départ planifié trouvé",
    "Not available while impersonating an employee":
      "Indisponible pendant l'emprunt d'identité",
    "Not found": "Introuvable",
    "Notification not found": "Notification introuvable",
    "Number sequence not found": "Séquence de numérotation introuvable",
    "Offer PDF not found": "PDF de l'offre introuvable",
    "Offer customer and currency must match":
      "Le client et la devise de l'offre doivent correspondre",
    "Offer line references require an offer as the source":
      "Les références aux lignes d'offre nécessitent une offre comme source",
    "Offer not found": "Offre introuvable",
    "One or more assigned users were not found":
      "Une ou plusieurs personnes attribuées sont introuvables",
    "One or more organization units were not found or inactive":
      "Une ou plusieurs unités sont introuvables ou inactives",
    "One or more organization users were not found":
      "Une ou plusieurs personnes sont introuvables",
    "One or more project members were not found":
      "Un ou plusieurs membres du projet sont introuvables",
    "One or more roles were not found":
      "Un ou plusieurs rôles sont introuvables",
    "Only JPEG, PNG, WebP and PDF files are supported":
      "Seuls les fichiers JPEG, PNG, WebP et PDF sont acceptés",
    "Only PDF, DOCX, JPEG, PNG and WebP files are supported":
      "Seuls les fichiers PDF, DOCX, JPEG, PNG et WebP sont acceptés",
    "Only a planned project can be deleted":
      "Seul un projet planifié peut être supprimé",
    "Only a submitted timesheet can be withdrawn":
      "Seule une feuille de temps soumise peut être retirée",
    "Only accepted offers can be invoiced":
      "Seules les offres acceptées peuvent être facturées",
    "Only active email-based users can receive a password reset":
      "Seules les personnes actives avec e-mail peuvent réinitialiser leur mot de passe",
    "Only administrators can change administrator accounts":
      "Seuls les administrateurs peuvent modifier des comptes administrateurs",
    "Only administrators can grant administrator rights":
      "Seuls les administrateurs peuvent accorder des droits d'administrateur",
    "Only an accepted offer can start a project":
      "Seule une offre acceptée peut démarrer un projet",
    "Only approved timesheets can be reopened":
      "Seules les feuilles de temps validées peuvent être rouvertes",
    "Only deactivated employees can be reactivated":
      "Seules les personnes désactivées peuvent être réactivées",
    "Only dead-lettered deliveries can be retried":
      "Seules les livraisons définitivement échouées peuvent être relancées",
    "Only draft expense claims can be changed":
      "Seuls les brouillons de notes de frais peuvent être modifiés",
    "Only draft expense claims can be submitted":
      "Seuls les brouillons de notes de frais peuvent être soumis",
    "Only draft invoices can be deleted":
      "Seuls les brouillons de factures peuvent être supprimés",
    "Only draft invoices can be issued":
      "Seuls les brouillons de factures peuvent être émis",
    "Only draft offers can be deleted":
      "Seuls les brouillons d'offres peuvent être supprimés",
    "Only draft offers can be edited. Create a revision to change a sent offer.":
      "Seuls les brouillons d'offres sont modifiables. Créez une révision pour changer une offre envoyée.",
    "Only invited users can receive a new invitation":
      "Seules les personnes invitées peuvent recevoir une nouvelle invitation",
    "Only issued invoices can be credited":
      "Seules les factures émises peuvent être créditées",
    "Only issued invoices can be shared":
      "Seules les factures émises peuvent être partagées",
    "Only packages can contain component items":
      "Seuls les lots peuvent contenir des composants",
    "Only packages can contain items":
      "Seuls les lots peuvent contenir des articles",
    "Only personal details can be changed":
      "Vous ne pouvez modifier que vos données personnelles",
    "Only submitted expense claims can be processed":
      "Seules les notes de frais soumises peuvent être traitées",
    "Only submitted expense claims can be withdrawn":
      "Seules les notes de frais soumises peuvent être retirées",
    "Only submitted timesheets can be approved or rejected":
      "Seules les feuilles de temps soumises peuvent être validées ou renvoyées",
    "Organization unit not found": "Unité introuvable",
    "Organization units cannot contain circular ancestry":
      "Les unités ne peuvent pas avoir une hiérarchie circulaire",
    "Package components must be active inventory items":
      "Les composants doivent être des articles de stock actifs",
    "Package items must be unique":
      "Chaque composant ne peut figurer qu'une fois",
    "Parent category not found": "Catégorie parente introuvable",
    "Parent organization unit not found": "Unité parente introuvable",
    "Parent project customer and currency must match":
      "Le client et la devise du projet parent doivent correspondre",
    "Parent project not found": "Projet parent introuvable",
    "Password must be at least 10 characters":
      "Le mot de passe doit contenir au moins 10 caractères",
    "Password must contain a letter":
      "Le mot de passe doit contenir une lettre",
    "Password must contain a number":
      "Le mot de passe doit contenir un chiffre",
    "Payment not found": "Paiement introuvable",
    "Payments can only be recorded on issued invoices":
      "Les paiements ne peuvent être saisis que sur des factures émises",
    "Payments cannot be recorded on credit notes":
      "Aucun paiement ne peut être saisi sur une note de crédit",
    "Payments on cancelled invoices cannot be changed":
      "Les paiements de factures annulées ne peuvent pas être modifiés",
    "Planned end must not be before planned start":
      "La fin prévue ne peut pas précéder le début prévu",
    "Planner assignment not found": "Intervention introuvable",
    "Planner range is required": "Une période est requise",
    "Planner range must cover 1 to 35 days":
      "La période doit couvrir 1 à 35 jours",
    "Project cost not found": "Coût de projet introuvable",
    "Project customer and currency must match the invoice":
      "Le client et la devise du projet doivent correspondre à la facture",
    "Project customer and currency must match the offer":
      "Le client et la devise du projet doivent correspondre à l'offre",
    "Project has related records; cancel it instead":
      "Le projet a déjà des écritures ; annulez-le plutôt",
    "Project hierarchy cannot contain a cycle":
      "La hiérarchie des projets ne peut pas être circulaire",
    "Project hierarchy is inconsistent":
      "La hiérarchie des projets est incohérente",
    "Project not found": "Projet introuvable",
    "Project time cannot exceed worked time for this day":
      "Le temps de projet ne peut pas dépasser le temps travaillé du jour",
    "QRR references require a QR-IBAN":
      "Les références QRR nécessitent un QR-IBAN",
    "Quantity must be greater than zero":
      "La quantité doit être supérieure à zéro",
    "Quantity must be nonnegative": "La quantité ne peut pas être négative",
    "Quantity must be positive": "La quantité doit être positive",
    "Receipt not found": "Justificatif introuvable",
    "Receipts can only be changed on draft claims":
      "Les justificatifs ne peuvent être modifiés que sur un brouillon",
    "Receipts of paid claims cannot be changed":
      "Les justificatifs d'une note payée ne peuvent pas être modifiés",
    "Removed members still have future schedule assignments":
      "Des membres retirés ont encore des interventions planifiées",
    "Request not found": "Demande introuvable",
    "Requested date is required": "Une date souhaitée est requise",
    "Requests with offers or invoices cannot be deleted. Cancel it instead.":
      "Une demande avec offres ou factures ne peut pas être supprimée. Annulez-la plutôt.",
    "Resolve stock, pending offers and active package usage before changing inventory tracking":
      "Réglez le stock, les offres en cours et les lots actifs avant de changer la gestion du stock",
    "Return quantity exceeds the source issue":
      "La quantité reprise dépasse la quantité sortie",
    "Role not found": "Rôle introuvable",
    "Roles assigned to users cannot be deleted":
      "Les rôles attribués ne peuvent pas être supprimés",
    "Session not found": "Session introuvable",
    "Setup has already been completed.": "La configuration est déjà terminée.",
    "Site address not found": "Adresse d'intervention introuvable",
    "Source and destination locations must differ":
      "Les emplacements source et destination doivent différer",
    "Source issue does not match the return":
      "La sortie d'origine ne correspond pas au retour",
    "Source request customer and currency must match":
      "Le client et la devise de la demande doivent correspondre",
    "Start and end time are both required":
      "Les heures de début et de fin sont requises",
    "Submitted or approved timesheets cannot be changed":
      "Les feuilles de temps soumises ou validées ne peuvent pas être modifiées",
    "System admin cannot be invited":
      "L'administrateur système ne peut pas être invité",
    "System admin has no email address":
      "L'administrateur système n'a pas d'adresse e-mail",
    "System administrator access is required":
      "Un accès administrateur système est requis",
    "System administrator session is unavailable":
      "La session de l'administrateur système est indisponible",
    "System roles cannot be deleted":
      "Les rôles système ne peuvent pas être supprimés",
    "Tax rate not found": "Taux de TVA introuvable",
    "Tenant setup has not been completed yet.":
      "La configuration n'est pas encore terminée.",
    "The AI provider is temporarily unavailable.":
      "Le service d'IA est temporairement indisponible.",
    "The AI provider returned an invalid response.":
      "Le service d'IA a renvoyé une réponse invalide.",
    "The assignment was modified by someone else":
      "L'intervention a été modifiée par quelqu'un d'autre",
    "The audio file is empty.": "Le fichier audio est vide.",
    "The corrected invoice is no longer open":
      "La facture corrigée n'est plus ouverte",
    "The credit note exceeds the amount still creditable on the invoice":
      "La note de crédit dépasse le montant encore créditable",
    "The customer cannot change once offers or invoices exist":
      "Le client ne peut plus être changé dès qu'il existe des offres ou factures",
    "The customer does not match the source document":
      "Le client ne correspond pas au document source",
    "The customer has no billing address":
      "Le client n'a pas d'adresse de facturation",
    "The customer has no email address": "Le client n'a pas d'adresse e-mail",
    "The customer needs a billing address":
      "Le client a besoin d'une adresse de facturation",
    "The customer was modified by someone else. Reload and retry.":
      "Le client a été modifié par quelqu'un d'autre. Rechargez et réessayez.",
    "The default location cannot be deactivated":
      "L'emplacement par défaut ne peut pas être désactivé",
    "The default location must be active":
      "L'emplacement par défaut doit être actif",
    "The default tax rate cannot be deleted":
      "Le taux de TVA par défaut ne peut pas être supprimé",
    "The existing organization hierarchy contains a cycle":
      "La hiérarchie existante contient une boucle",
    "The file contents do not match the selected file type":
      "Le contenu du fichier ne correspond pas à son type",
    "The image could not be processed. Please upload a JPEG or PNG.":
      "L'image n'a pas pu être traitée. Veuillez envoyer un JPEG ou PNG.",
    "The invoice has payments. Issue a credit note instead of cancelling.":
      "La facture a des paiements. Émettez une note de crédit au lieu d'annuler.",
    "The invoice total must be greater than zero":
      "Le total de la facture doit être supérieur à zéro",
    "The invoice was already partially credited":
      "La facture a déjà été partiellement créditée",
    "The invoice was already sent": "La facture a déjà été envoyée",
    "The invoice was modified by someone else. Reload and try again.":
      "La facture a été modifiée par quelqu'un d'autre. Rechargez et réessayez.",
    "The invoiced quantity exceeds what is left to invoice on the offer":
      "La quantité dépasse ce qu'il reste à facturer sur l'offre",
    "The last active administrator cannot be deactivated":
      "Le dernier administrateur actif ne peut pas être désactivé",
    "The last active administrator cannot be offboarded":
      "Le dernier administrateur actif ne peut pas quitter l'entreprise",
    "The monthly AI budget has been exhausted.":
      "Le budget IA mensuel est épuisé.",
    "The new email address must differ from the current one":
      "La nouvelle adresse e-mail doit différer de l'actuelle",
    "The offer already belongs to a project":
      "L'offre appartient déjà à un projet",
    "The offer cannot be decided in its current status":
      "Aucune décision n'est possible sur l'offre dans son statut actuel",
    "The offer cannot be revised in its current status":
      "L'offre ne peut pas être révisée dans son statut actuel",
    "The offer cannot be sent in its current status":
      "L'offre ne peut pas être envoyée dans son statut actuel",
    "The offer was already sent": "L'offre a déjà été envoyée",
    "The offer was modified by someone else. Reload and try again.":
      "L'offre a été modifiée par quelqu'un d'autre. Rechargez et réessayez.",
    "The organization unit was modified by someone else":
      "L'unité a été modifiée par quelqu'un d'autre",
    "The payment was modified by someone else":
      "Le paiement a été modifié par quelqu'un d'autre",
    "The project belongs to another customer":
      "Le projet appartient à un autre client",
    "The project budget was modified by someone else":
      "Le budget du projet a été modifié par quelqu'un d'autre",
    "The project cost was modified by someone else":
      "Le coût du projet a été modifié par quelqu'un d'autre",
    "The project was modified by someone else":
      "Le projet a été modifié par quelqu'un d'autre",
    "The request contains invalid fields.": "Certains champs sont invalides.",
    "The request was modified by someone else. Reload and try again.":
      "La demande a été modifiée par quelqu'un d'autre. Rechargez et réessayez.",
    "The source request is already assigned to a project":
      "La demande est déjà rattachée à un projet",
    "The system administrator account cannot be changed here":
      "Le compte administrateur système ne peut pas être modifié ici",
    "The time allocation was modified by someone else":
      "L'imputation de temps a été modifiée par quelqu'un d'autre",
    "The timesheet is not editable": "La feuille de temps n'est pas modifiable",
    "The timesheet owner is not a project member":
      "La personne n'est pas membre du projet",
    "The wildcard event cannot be combined with other events":
      "« Tous les événements » ne peut pas être combiné avec d'autres",
    "This customer has requests, offers, projects or invoices and cannot be deleted. Archive the customer instead.":
      "Ce client a des demandes, offres, projets ou factures et ne peut pas être supprimé. Archivez-le plutôt.",
    "This status change is not possible":
      "Ce changement de statut n'est pas possible",
    "Thumbnail not found": "Miniature introuvable",
    "Time allocation not found": "Imputation introuvable",
    "Time can only be assigned to your projects":
      "Le temps ne peut être imputé qu'à vos projets",
    "Time entry not found": "Saisie de temps introuvable",
    "Time entry was changed by someone else":
      "La saisie de temps a été modifiée par quelqu'un d'autre",
    "Time must use HH:mm": "L'heure doit être au format HH:mm",
    "Timesheet not found": "Feuille de temps introuvable",
    "Timesheet was changed; reload before deciding":
      "La feuille de temps a changé ; rechargez avant de décider",
    "Timesheet was changed; reload before saving":
      "La feuille de temps a changé ; rechargez avant d'enregistrer",
    "Timesheet was changed; reload before submitting":
      "La feuille de temps a changé ; rechargez avant de soumettre",
    "Unit not found": "Unité introuvable",
    "Unknown permission": "Autorisation inconnue",
    "Unsupported audio type. Use WAV, MP3, M4A, OGG, WebM, or FLAC.":
      "Format audio non pris en charge. Utilisez WAV, MP3, M4A, OGG, WebM ou FLAC.",
    "Unsupported webhook event": "Événement de webhook non pris en charge",
    "Use a hex colour like #0f766e":
      "Utilisez une couleur hexadécimale comme #0f766e",
    "User not found": "Utilisateur introuvable",
    "Vacation booking exceeds the available balance":
      "La réservation dépasse le solde de vacances disponible",
    "Webhook URL must not contain credentials":
      "L'URL du webhook ne doit pas contenir d'identifiants",
    "Webhook URL must use HTTP or HTTPS":
      "L'URL du webhook doit utiliser HTTP ou HTTPS",
    "Webhook delivery not found": "Livraison de webhook introuvable",
    "Webhook endpoint not found": "Point de terminaison introuvable",
    "Webhook endpoint was changed; reload before saving":
      "Le webhook a été modifié ; rechargez avant d'enregistrer",
    "You are already clocked in": "Vous avez déjà pointé l'entrée",
    "You are not clocked in": "Vous n'avez pas pointé l'entrée",
    "You cannot deactivate your own account":
      "Vous ne pouvez pas désactiver votre propre compte",
    "You cannot grant permissions you do not have":
      "Vous ne pouvez pas accorder des droits que vous n'avez pas",
    "You cannot offboard your own account":
      "Vous ne pouvez pas vous faire partir vous-même",
    "month must use the YYYY-MM format": "Le mois doit être au format AAAA-MM",
  },
  es: {
    "A QR-IBAN requires QRR references": "Un QR-IBAN requiere referencias QRR",
    "A catalog item with this SKU already exists":
      "Ya existe un artículo con esta referencia",
    "A catalog item with this barcode already exists":
      "Ya existe un artículo con este código de barras",
    "A company holiday already exists on this date":
      "Ya existe un festivo en esta fecha",
    "A day of month is required": "Se requiere un día del mes",
    "A document file is required": "Se requiere un archivo",
    "A file is required": "Se requiere un archivo",
    "A lead must be a member of the organization unit":
      "Un responsable debe ser miembro de la unidad",
    "A location with inventory cannot be deactivated":
      "Una ubicación con stock no se puede desactivar",
    "A logo file is required": "Se requiere un archivo de logotipo",
    "A month is required": "Se requiere un mes",
    "A newer revision of this offer exists; decide on the latest revision":
      "Existe una revisión más reciente de esta oferta; decide sobre la última",
    "A number sequence is not configured":
      "Una secuencia de numeración no está configurada",
    "A package cannot contain itself":
      "Un paquete no puede contenerse a sí mismo",
    "A project cannot be its own parent":
      "Un proyecto no puede ser su propio padre",
    "A reason is required": "Se requiere un motivo",
    "A receipt file is required": "Se requiere un recibo",
    "A receipt unit cost is required": "Se requiere un coste unitario",
    "A rejection reason is required": "Se requiere un motivo de rechazo",
    "A revision of this offer already exists":
      "Ya existe una revisión de esta oferta",
    "A role with this name already exists": "Ya existe un rol con este nombre",
    "A terminal project cannot be scheduled":
      "Un proyecto finalizado o cancelado no se puede planificar",
    "A terminal project cannot receive time allocations":
      "No se puede imputar tiempo a un proyecto finalizado o cancelado",
    "A terminal project's time cannot be changed":
      "El tiempo de un proyecto finalizado o cancelado no se puede cambiar",
    "A unit with this code already exists":
      "Ya existe una unidad con este código",
    "A user cannot be both a manager and a member":
      "Una persona no puede ser responsable y miembro a la vez",
    "A user with this email already exists":
      "Ya existe un usuario con este correo electrónico",
    "A weekday is required": "Se requiere un día de la semana",
    "AI assistance is not configured.":
      "La asistencia de IA no está configurada.",
    "API key not found": "Clave API no encontrada",
    "Add at least one expense before submitting":
      "Añade al menos un gasto antes de enviar",
    "Add at least one priced line before issuing":
      "Añade al menos una línea con precio antes de emitir",
    "Add at least one priced line before sending the offer":
      "Añade al menos una línea con precio antes de enviar la oferta",
    "Administrator access is required": "Se requiere acceso de administrador",
    "All expense items must use the claim currency":
      "Todas las partidas deben usar la moneda de la nota",
    "Allocation date must fall within the timesheet week":
      "La fecha debe estar dentro de la semana de la hoja de horas",
    "Amount must be a non-negative decimal":
      "El importe debe ser un número positivo",
    "Amount must be greater than zero": "El importe debe ser mayor que cero",
    "Amount must be nonnegative": "El importe no puede ser negativo",
    "An audio file is required in the file field.":
      "Se requiere un archivo de audio.",
    "An invoice line references a line of another offer":
      "Una línea de factura hace referencia a otra oferta",
    "An ordinal is required": "Se requiere un ordinal (primero, segundo…)",
    "An organization unit cannot contain itself":
      "Una unidad no puede contenerse a sí misma",
    "Assignment range must contain between 1 and 42 days":
      "El periodo debe tener entre 1 y 42 días",
    "Attachment not found": "Adjunto no encontrado",
    "Attendance intervals cannot overlap":
      "Los intervalos de asistencia no pueden solaparse",
    "Audio file is too large.": "El archivo de audio es demasiado grande.",
    "Billing settings not found": "Configuración de facturación no encontrada",
    "Calendar end must not precede start":
      "El final no puede ser anterior al inicio",
    "Calendar event end must be after start":
      "El final debe ser posterior al inicio",
    "Calendar feed not found": "Suscripción de calendario no encontrada",
    "Calendar range cannot exceed one year":
      "El periodo no puede superar un año",
    "Catalog item is not enabled for stock":
      "Este artículo no se gestiona en stock",
    "Catalog item not found": "Artículo no encontrado",
    "Categories support only one subcategory level":
      "Las categorías solo admiten un nivel de subcategorías",
    "Category not found": "Categoría no encontrada",
    "Choose either an offer or a request as the source":
      "Elige una oferta o una solicitud como origen",
    "Clock-out time must be after clock-in time":
      "La hora de salida debe ser posterior a la de entrada",
    "Closed requests cannot be edited. Reopen it first.":
      "Una solicitud cerrada no se puede editar. Reábrela primero.",
    "Company holiday not found": "Festivo no encontrado",
    "Company logo not found": "Logotipo de la empresa no encontrado",
    "Company name is required for company customers":
      "El nombre de la empresa es obligatorio para clientes empresa",
    "Company settings not found": "Configuración de la empresa no encontrada",
    "Completed or cancelled projects cannot receive time entries":
      "No se puede registrar tiempo en proyectos finalizados o cancelados",
    "Configure the bank account (IBAN) in the billing settings before issuing invoices":
      "Configura la cuenta bancaria (IBAN) en la facturación antes de emitir facturas",
    "Credit notes can only correct invoices":
      "Las notas de crédito solo pueden corregir facturas",
    "Customer not found": "Cliente no encontrado",
    "Customer, request or accepted offer is required":
      "Se requiere un cliente, una solicitud o una oferta aceptada",
    "Date is not valid": "La fecha no es válida",
    "Date must use YYYY-MM-DD": "La fecha debe tener el formato AAAA-MM-DD",
    "Deactivated employees must be reactivated instead":
      "Los empleados desactivados deben reactivarse",
    "Default inventory location is inactive or missing":
      "La ubicación predeterminada está inactiva o no existe",
    "Discount cannot exceed 100%": "El descuento no puede superar el 100 %",
    "Document category not found": "Categoría de documento no encontrada",
    "Document not found": "Documento no encontrado",
    "Document settings not found": "Configuración de documentos no encontrada",
    "Duration is required": "Se requiere una duración",
    "Each weekday is required once":
      "Cada día de la semana debe figurar una vez",
    "Email confirmation link is invalid or has expired":
      "El enlace de confirmación no es válido o ha caducado",
    "Employee document not found": "Documento no encontrado",
    "Employee is already deactivated": "La persona ya está desactivada",
    "Employee not found": "Empleado no encontrado",
    "Employee number or AHV/AVS number is already assigned":
      "El número de personal o AVS ya está asignado",
    "Employment end must not precede employment start":
      "El fin del empleo no puede ser anterior al inicio",
    "End time must be after start time":
      "La hora de fin debe ser posterior a la de inicio",
    "Everything on this offer has already been invoiced":
      "Esta oferta ya se ha facturado por completo",
    "Expense claim not found": "Nota de gastos no encontrada",
    "Expense claim was changed; reload before saving":
      "La nota de gastos ha cambiado; recarga antes de guardar",
    "Failed email not found": "Correo fallido no encontrado",
    "File not found": "Archivo no encontrado",
    "Financial request details require finance access":
      "Los detalles financieros requieren acceso a finanzas",
    "Fixed amount is required for fixed-price requests":
      "Se requiere un importe para precio fijo",
    "Insufficient inventory on hand": "Stock insuficiente",
    "Insufficient permissions": "No tienes los permisos necesarios",
    "Insufficient permissions to assign requests":
      "No puedes asignar solicitudes",
    "Invalid AHV/AVS number": "Número AVS no válido",
    "Invalid IBAN checksum": "El IBAN no es válido (dígito de control)",
    "Invalid balance year": "Año no válido",
    "Invalid decimal value": "Número no válido",
    "Invalid login identifier or password": "Usuario o contraseña incorrectos",
    "Invalid or expired setup invitation":
      "La invitación de configuración no es válida o ha caducado",
    "Invalid phone number": "Número de teléfono no válido",
    "Invalid setup token": "Token de configuración no válido",
    "Invalid year": "Año no válido",
    "Inventory balance not found": "Stock no encontrado",
    "Inventory cannot be issued to a terminal project":
      "No se puede entregar material a un proyecto finalizado o cancelado",
    "Inventory cannot be returned to a terminal project":
      "No se puede devolver material de un proyecto finalizado o cancelado",
    "Inventory location not found": "Ubicación no encontrada",
    "Invitation is invalid or has expired":
      "La invitación no es válida o ha caducado",
    "Invoice PDF not found": "PDF de la factura no encontrado",
    "Invoice not found": "Factura no encontrada",
    "Invoice share link not found": "Enlace compartido no encontrado",
    "Issued invoices cannot be edited. Issue a credit note to correct them.":
      "Una factura emitida no se puede editar. Corrígela con una nota de crédito.",
    "Localization settings not found": "Configuración regional no encontrada",
    "Login identifier is required": "Se requiere usuario o correo",
    "Logo must be a JPEG, PNG or WebP image":
      "El logotipo debe ser una imagen JPEG, PNG o WebP",
    "Mileage expenses require kilometers":
      "Los gastos de kilometraje requieren kilómetros",
    "Missing or invalid CSRF token": "La sesión ha caducado; recarga la página",
    "New password must differ from the current password":
      "La nueva contraseña debe ser distinta de la actual",
    "No impersonation session is active":
      "No hay ninguna sesión de suplantación activa",
    "No scheduled offboarding found": "No hay ninguna baja planificada",
    "Not available while impersonating an employee":
      "No disponible durante la suplantación",
    "Not found": "No encontrado",
    "Notification not found": "Notificación no encontrada",
    "Number sequence not found": "Secuencia de numeración no encontrada",
    "Offer PDF not found": "PDF de la oferta no encontrado",
    "Offer customer and currency must match":
      "El cliente y la moneda de la oferta deben coincidir",
    "Offer line references require an offer as the source":
      "Las referencias a líneas de oferta requieren una oferta como origen",
    "Offer not found": "Oferta no encontrada",
    "One or more assigned users were not found":
      "No se encontraron una o más personas asignadas",
    "One or more organization units were not found or inactive":
      "Una o más unidades no existen o están inactivas",
    "One or more organization users were not found":
      "No se encontraron una o más personas",
    "One or more project members were not found":
      "No se encontraron uno o más miembros del proyecto",
    "One or more roles were not found": "No se encontraron uno o más roles",
    "Only JPEG, PNG, WebP and PDF files are supported":
      "Solo se admiten archivos JPEG, PNG, WebP y PDF",
    "Only PDF, DOCX, JPEG, PNG and WebP files are supported":
      "Solo se admiten archivos PDF, DOCX, JPEG, PNG y WebP",
    "Only a planned project can be deleted":
      "Solo se puede eliminar un proyecto planificado",
    "Only a submitted timesheet can be withdrawn":
      "Solo se puede retirar una hoja de horas enviada",
    "Only accepted offers can be invoiced":
      "Solo se pueden facturar ofertas aceptadas",
    "Only active email-based users can receive a password reset":
      "Solo las personas activas con correo pueden restablecer la contraseña",
    "Only administrators can change administrator accounts":
      "Solo los administradores pueden modificar cuentas de administrador",
    "Only administrators can grant administrator rights":
      "Solo los administradores pueden conceder derechos de administrador",
    "Only an accepted offer can start a project":
      "Solo una oferta aceptada puede iniciar un proyecto",
    "Only approved timesheets can be reopened":
      "Solo se pueden reabrir hojas de horas aprobadas",
    "Only deactivated employees can be reactivated":
      "Solo se pueden reactivar personas desactivadas",
    "Only dead-lettered deliveries can be retried":
      "Solo se pueden reintentar entregas fallidas definitivamente",
    "Only draft expense claims can be changed":
      "Solo se pueden modificar borradores de notas de gastos",
    "Only draft expense claims can be submitted":
      "Solo se pueden enviar borradores de notas de gastos",
    "Only draft invoices can be deleted":
      "Solo se pueden eliminar borradores de facturas",
    "Only draft invoices can be issued":
      "Solo se pueden emitir borradores de facturas",
    "Only draft offers can be deleted":
      "Solo se pueden eliminar borradores de ofertas",
    "Only draft offers can be edited. Create a revision to change a sent offer.":
      "Solo se pueden editar borradores de ofertas. Crea una revisión para cambiar una oferta enviada.",
    "Only invited users can receive a new invitation":
      "Solo las personas invitadas pueden recibir una nueva invitación",
    "Only issued invoices can be credited":
      "Solo se pueden abonar facturas emitidas",
    "Only issued invoices can be shared":
      "Solo se pueden compartir facturas emitidas",
    "Only packages can contain component items":
      "Solo los paquetes pueden contener componentes",
    "Only packages can contain items":
      "Solo los paquetes pueden contener artículos",
    "Only personal details can be changed":
      "Solo puedes cambiar tus datos personales",
    "Only submitted expense claims can be processed":
      "Solo se pueden tramitar notas de gastos enviadas",
    "Only submitted expense claims can be withdrawn":
      "Solo se pueden retirar notas de gastos enviadas",
    "Only submitted timesheets can be approved or rejected":
      "Solo se pueden aprobar o devolver hojas de horas enviadas",
    "Organization unit not found": "Unidad no encontrada",
    "Organization units cannot contain circular ancestry":
      "Las unidades no pueden tener una jerarquía circular",
    "Package components must be active inventory items":
      "Los componentes deben ser artículos de stock activos",
    "Package items must be unique":
      "Cada componente solo puede figurar una vez",
    "Parent category not found": "Categoría superior no encontrada",
    "Parent organization unit not found": "Unidad superior no encontrada",
    "Parent project customer and currency must match":
      "El cliente y la moneda del proyecto principal deben coincidir",
    "Parent project not found": "Proyecto principal no encontrado",
    "Password must be at least 10 characters":
      "La contraseña debe tener al menos 10 caracteres",
    "Password must contain a letter": "La contraseña debe contener una letra",
    "Password must contain a number": "La contraseña debe contener un número",
    "Payment not found": "Pago no encontrado",
    "Payments can only be recorded on issued invoices":
      "Los pagos solo se pueden registrar en facturas emitidas",
    "Payments cannot be recorded on credit notes":
      "No se pueden registrar pagos en notas de crédito",
    "Payments on cancelled invoices cannot be changed":
      "Los pagos de facturas anuladas no se pueden cambiar",
    "Planned end must not be before planned start":
      "El fin previsto no puede ser anterior al inicio previsto",
    "Planner assignment not found": "Asignación no encontrada",
    "Planner range is required": "Se requiere un periodo",
    "Planner range must cover 1 to 35 days":
      "El periodo debe abarcar de 1 a 35 días",
    "Project cost not found": "Coste de proyecto no encontrado",
    "Project customer and currency must match the invoice":
      "El cliente y la moneda del proyecto deben coincidir con la factura",
    "Project customer and currency must match the offer":
      "El cliente y la moneda del proyecto deben coincidir con la oferta",
    "Project has related records; cancel it instead":
      "El proyecto ya tiene registros; cancélalo en su lugar",
    "Project hierarchy cannot contain a cycle":
      "La jerarquía de proyectos no puede ser circular",
    "Project hierarchy is inconsistent":
      "La jerarquía de proyectos es incoherente",
    "Project not found": "Proyecto no encontrado",
    "Project time cannot exceed worked time for this day":
      "El tiempo de proyecto no puede superar el tiempo trabajado del día",
    "QRR references require a QR-IBAN":
      "Las referencias QRR requieren un QR-IBAN",
    "Quantity must be greater than zero": "La cantidad debe ser mayor que cero",
    "Quantity must be nonnegative": "La cantidad no puede ser negativa",
    "Quantity must be positive": "La cantidad debe ser positiva",
    "Receipt not found": "Recibo no encontrado",
    "Receipts can only be changed on draft claims":
      "Los recibos solo se pueden cambiar en borradores",
    "Receipts of paid claims cannot be changed":
      "Los recibos de notas pagadas no se pueden cambiar",
    "Removed members still have future schedule assignments":
      "Los miembros eliminados aún tienen asignaciones futuras",
    "Request not found": "Solicitud no encontrada",
    "Requested date is required": "Se requiere una fecha deseada",
    "Requests with offers or invoices cannot be deleted. Cancel it instead.":
      "Una solicitud con ofertas o facturas no se puede eliminar. Cancélala en su lugar.",
    "Resolve stock, pending offers and active package usage before changing inventory tracking":
      "Resuelve el stock, las ofertas pendientes y los paquetes activos antes de cambiar la gestión de stock",
    "Return quantity exceeds the source issue":
      "La cantidad devuelta supera la entregada",
    "Role not found": "Rol no encontrado",
    "Roles assigned to users cannot be deleted":
      "Los roles asignados no se pueden eliminar",
    "Session not found": "Sesión no encontrada",
    "Setup has already been completed.":
      "La configuración ya se ha completado.",
    "Site address not found": "Dirección de obra no encontrada",
    "Source and destination locations must differ":
      "Las ubicaciones de origen y destino deben ser distintas",
    "Source issue does not match the return":
      "La entrega de origen no coincide con la devolución",
    "Source request customer and currency must match":
      "El cliente y la moneda de la solicitud deben coincidir",
    "Start and end time are both required":
      "Se requieren hora de inicio y de fin",
    "Submitted or approved timesheets cannot be changed":
      "Las hojas de horas enviadas o aprobadas no se pueden cambiar",
    "System admin cannot be invited":
      "El administrador del sistema no se puede invitar",
    "System admin has no email address":
      "El administrador del sistema no tiene correo",
    "System administrator access is required":
      "Se requiere acceso de administrador del sistema",
    "System administrator session is unavailable":
      "La sesión del administrador del sistema no está disponible",
    "System roles cannot be deleted":
      "Los roles del sistema no se pueden eliminar",
    "Tax rate not found": "Tipo de IVA no encontrado",
    "Tenant setup has not been completed yet.":
      "La configuración aún no se ha completado.",
    "The AI provider is temporarily unavailable.":
      "El servicio de IA no está disponible temporalmente.",
    "The AI provider returned an invalid response.":
      "El servicio de IA devolvió una respuesta no válida.",
    "The assignment was modified by someone else":
      "Otra persona ha modificado la asignación",
    "The audio file is empty.": "El archivo de audio está vacío.",
    "The corrected invoice is no longer open":
      "La factura corregida ya no está abierta",
    "The credit note exceeds the amount still creditable on the invoice":
      "La nota de crédito supera el importe aún abonable",
    "The customer cannot change once offers or invoices exist":
      "El cliente no se puede cambiar si ya existen ofertas o facturas",
    "The customer does not match the source document":
      "El cliente no coincide con el documento de origen",
    "The customer has no billing address":
      "El cliente no tiene dirección de facturación",
    "The customer has no email address":
      "El cliente no tiene correo electrónico",
    "The customer needs a billing address":
      "El cliente necesita una dirección de facturación",
    "The customer was modified by someone else. Reload and retry.":
      "Otra persona ha modificado el cliente. Recarga e inténtalo de nuevo.",
    "The default location cannot be deactivated":
      "La ubicación predeterminada no se puede desactivar",
    "The default location must be active":
      "La ubicación predeterminada debe estar activa",
    "The default tax rate cannot be deleted":
      "El tipo de IVA predeterminado no se puede eliminar",
    "The existing organization hierarchy contains a cycle":
      "La jerarquía existente contiene un ciclo",
    "The file contents do not match the selected file type":
      "El contenido del archivo no coincide con su tipo",
    "The image could not be processed. Please upload a JPEG or PNG.":
      "No se pudo procesar la imagen. Sube un JPEG o PNG.",
    "The invoice has payments. Issue a credit note instead of cancelling.":
      "La factura tiene pagos. Emite una nota de crédito en lugar de anularla.",
    "The invoice total must be greater than zero":
      "El total de la factura debe ser mayor que cero",
    "The invoice was already partially credited":
      "La factura ya se abonó parcialmente",
    "The invoice was already sent": "La factura ya se envió",
    "The invoice was modified by someone else. Reload and try again.":
      "Otra persona ha modificado la factura. Recarga e inténtalo de nuevo.",
    "The invoiced quantity exceeds what is left to invoice on the offer":
      "La cantidad supera lo que queda por facturar en la oferta",
    "The last active administrator cannot be deactivated":
      "El último administrador activo no se puede desactivar",
    "The last active administrator cannot be offboarded":
      "El último administrador activo no se puede dar de baja",
    "The monthly AI budget has been exhausted.":
      "El presupuesto mensual de IA se ha agotado.",
    "The new email address must differ from the current one":
      "La nueva dirección de correo debe ser distinta de la actual",
    "The offer already belongs to a project":
      "La oferta ya pertenece a un proyecto",
    "The offer cannot be decided in its current status":
      "No se puede decidir sobre la oferta en su estado actual",
    "The offer cannot be revised in its current status":
      "La oferta no se puede revisar en su estado actual",
    "The offer cannot be sent in its current status":
      "La oferta no se puede enviar en su estado actual",
    "The offer was already sent": "La oferta ya se envió",
    "The offer was modified by someone else. Reload and try again.":
      "Otra persona ha modificado la oferta. Recarga e inténtalo de nuevo.",
    "The organization unit was modified by someone else":
      "Otra persona ha modificado la unidad",
    "The payment was modified by someone else":
      "Otra persona ha modificado el pago",
    "The project belongs to another customer":
      "El proyecto pertenece a otro cliente",
    "The project budget was modified by someone else":
      "Otra persona ha modificado el presupuesto del proyecto",
    "The project cost was modified by someone else":
      "Otra persona ha modificado el coste del proyecto",
    "The project was modified by someone else":
      "Otra persona ha modificado el proyecto",
    "The request contains invalid fields.": "Algunos campos no son válidos.",
    "The request was modified by someone else. Reload and try again.":
      "Otra persona ha modificado la solicitud. Recarga e inténtalo de nuevo.",
    "The source request is already assigned to a project":
      "La solicitud ya está asignada a un proyecto",
    "The system administrator account cannot be changed here":
      "La cuenta del administrador del sistema no se puede cambiar aquí",
    "The time allocation was modified by someone else":
      "Otra persona ha modificado la imputación de tiempo",
    "The timesheet is not editable": "La hoja de horas no es editable",
    "The timesheet owner is not a project member":
      "La persona no es miembro del proyecto",
    "The wildcard event cannot be combined with other events":
      "«Todos los eventos» no se puede combinar con otros",
    "This customer has requests, offers, projects or invoices and cannot be deleted. Archive the customer instead.":
      "Este cliente tiene solicitudes, ofertas, proyectos o facturas y no se puede eliminar. Archívalo en su lugar.",
    "This status change is not possible": "Este cambio de estado no es posible",
    "Thumbnail not found": "Miniatura no encontrada",
    "Time allocation not found": "Imputación no encontrada",
    "Time can only be assigned to your projects":
      "Solo puedes imputar tiempo a tus proyectos",
    "Time entry not found": "Registro de tiempo no encontrado",
    "Time entry was changed by someone else":
      "Otra persona ha modificado el registro de tiempo",
    "Time must use HH:mm": "La hora debe tener el formato HH:mm",
    "Timesheet not found": "Hoja de horas no encontrada",
    "Timesheet was changed; reload before deciding":
      "La hoja de horas ha cambiado; recarga antes de decidir",
    "Timesheet was changed; reload before saving":
      "La hoja de horas ha cambiado; recarga antes de guardar",
    "Timesheet was changed; reload before submitting":
      "La hoja de horas ha cambiado; recarga antes de enviar",
    "Unit not found": "Unidad no encontrada",
    "Unknown permission": "Permiso desconocido",
    "Unsupported audio type. Use WAV, MP3, M4A, OGG, WebM, or FLAC.":
      "Formato de audio no compatible. Usa WAV, MP3, M4A, OGG, WebM o FLAC.",
    "Unsupported webhook event": "Evento de webhook no compatible",
    "Use a hex colour like #0f766e": "Usa un color hexadecimal como #0f766e",
    "User not found": "Usuario no encontrado",
    "Vacation booking exceeds the available balance":
      "La reserva supera el saldo de vacaciones disponible",
    "Webhook URL must not contain credentials":
      "La URL del webhook no debe contener credenciales",
    "Webhook URL must use HTTP or HTTPS":
      "La URL del webhook debe usar HTTP o HTTPS",
    "Webhook delivery not found": "Entrega de webhook no encontrada",
    "Webhook endpoint not found": "Destino del webhook no encontrado",
    "Webhook endpoint was changed; reload before saving":
      "El webhook ha cambiado; recarga antes de guardar",
    "You are already clocked in": "Ya has fichado la entrada",
    "You are not clocked in": "No has fichado la entrada",
    "You cannot deactivate your own account":
      "No puedes desactivar tu propia cuenta",
    "You cannot grant permissions you do not have":
      "No puedes conceder permisos que no tienes",
    "You cannot offboard your own account":
      "No puedes darte de baja a ti mismo",
    "month must use the YYYY-MM format": "El mes debe tener el formato AAAA-MM",
  },
};
